import { useEffect, useRef } from 'react'
import type { ThreadForProgramme } from '@/hooks/useThreadForProgramme'

interface AddToExistingChatModalProps {
  isOpen: boolean
  onClose: () => void
  onYes: () => void
  onNoCreateNew: () => void
  /** Existing thread and group from useThreadForProgramme */
  existing: ThreadForProgramme
}

export default function AddToExistingChatModal({
  isOpen,
  onClose,
  onYes,
  onNoCreateNew,
  existing,
}: AddToExistingChatModalProps) {
  const firstButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen && firstButtonRef.current) {
      firstButtonRef.current.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  const { group, thread } = existing

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-to-existing-chat-title"
    >
      <div
        className="rounded-lg p-6 max-w-md w-full"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            id="add-to-existing-chat-title"
            className="text-xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Add to existing chat?
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          A thread for this programme already exists. Add your comment?
        </p>

        <div
          className="rounded border-2 p-3 mb-4"
          style={{
            borderColor: 'var(--bg-tertiary)',
            backgroundColor: 'var(--bg-primary)',
          }}
        >
          <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            {group.name}
          </p>
          {group.description && (
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {group.description}
            </p>
          )}
          <p className="text-sm mt-2 font-medium" style={{ color: 'var(--text-primary)' }}>
            {thread.title}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            ref={firstButtonRef}
            type="button"
            onClick={onYes}
            className="w-full px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={onNoCreateNew}
            className="w-full px-4 py-2 font-semibold rounded border-2"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            No, create New
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
